import { createElement, useContext, Fragment } from '@wordpress/element'
import cls from 'classnames'
import Sortable from './Sortable'
import { DragDropContext } from './BuilderRoot'
import { PanelContext } from '../../../options/components/PanelLevel'
import { customItemsSeparator, getOriginalId } from './helpers'
import { capitalizeFirstLetter } from '../../../options/helpers/get-label'

const isItemAllowedIn = (itemId, barId, currentView) => {
	const item = ct_customizer_localizations.header_builder_data.header.find(
		({ id }) => id === getOriginalId(itemId)
	)

	if (!item) {
		return true
	}

	let allowed_in = []

	if (Array.isArray(item.config.allowed_in)) {
		allowed_in = item.config.allowed_in
	} else {
		if (item.config.allowed_in[currentView]) {
			allowed_in = item.config.allowed_in[currentView]
		}
	}

	const allowedRespected =
		allowed_in.length > 0 ? allowed_in.indexOf(barId) > -1 : true

	const exclusionRespected =
		item.config.excluded_from.length > 0
			? item.config.excluded_from.indexOf(barId) === -1
			: true

	return allowedRespected && exclusionRespected
}

export const DraggableItem = ({
	item,
	index,
	panelType,
	onRemove,
	renderItem,
	onClick,
	className,
}) => {
	const itemData = ct_customizer_localizations.header_builder_data[
		panelType
	].find(({ id }) => id === getOriginalId(item))

	const { builderValue } = useContext(DragDropContext)

	if (renderItem) {
		return renderItem({ item, itemData, index })
	}

	/**
	 * Dynamic items have a : in their ID
	 */
	const allDynamicItems = builderValue.items
		.filter(({ id }) => id.indexOf(customItemsSeparator()) > -1)
		.map(({ id }) => id)

	const allClonesAndOriginal = [
		getOriginalId(item),
		...allDynamicItems.filter(
			(id) => getOriginalId(id) === getOriginalId(item)
		),
	]

	const beautyItem = capitalizeFirstLetter(getOriginalId(item))

	return (
		<div
			data-id={item}
			className={cls('ct-builder-item', className, {
				// 'ct-is-dragging': snapshot.isDragging
			})}
			onClick={onClick}>
			{allClonesAndOriginal.length > 1
				? `${itemData?.config?.name ?? beautyItem} ${
						allClonesAndOriginal.indexOf(item) + 1
				  }`
				: itemData?.config?.name ?? beautyItem}

			<button
				className="ct-btn-remove"
				onClick={(e) => {
					e.preventDefault()
					e.stopPropagation()
					onRemove()
				}}></button>
		</div>
	)
}

const DraggableItems = ({
	items,
	draggableId,
	hasPointers = true,
	className,
	tagName = 'div',
	direction = 'horizontal',
	group = 'header_sortables',
	options = {},
	propsForItem = (item) => ({}),
	displayWrapper = true,
	...props
}) => {
	const { currentView, isDragging, setIsDragging, onChange, setList } =
		useContext(DragDropContext)

	const { panelsHelpers } = useContext(PanelContext)

	const itemsOutput = items.map((item, index) => (
		<DraggableItem
			key={item}
			index={index}
			panelType={'header'}
			item={item}
			className={
				panelsHelpers.isOpenFor(`builder_panel_${item}`)
					? 'ct-customizing'
					: ''
			}
			onClick={(e) => {
				e.stopPropagation()
				e.preventDefault()

				if (isDragging) {
					return
				}

				panelsHelpers.open(`builder_panel_${item}`)
			}}
			onRemove={() => {
				panelsHelpers.close()

				setTimeout(() => {
					setList({
						[draggableId]: items.filter((id) => id !== item),

						...(currentView === 'desktop' && item === 'trigger'
							? {
									['offcanvas:start']: [],
							  }
							: {}),
					})
				})
			}}
			{...propsForItem(item)}
		/>
	))

	if (!displayWrapper) {
		return <Fragment>{itemsOutput}</Fragment>
	}

	return (
		<Sortable
			options={{
				delay: 0,
				group,
				fallbackOnBody: true,
				forceFallback: true,
				fallbackTolerance: 5,
				filter: '.ct-pointer, .ct-btn-remove',
				direction,
				touchStartThreshold: 5,
				onStart: (event) => {
					setIsDragging(event.item.dataset.id)

					let currentView = wp.customize.previewedDevice()

					if (currentView === 'tablet') {
						currentView = 'mobile'
					}

					if (
						isItemAllowedIn(
							event.item.dataset.id,
							'middle-row',
							currentView
						)
					) {
						document.body.classList.add('ct-builder-dragging')
					}

					if (event.from && group && group.pull !== 'clone') {
						event.to.classList.add('ct-is-over')
					}
				},

				onEnd: () => {
					setIsDragging(false)
					document.body.classList.remove('ct-builder-dragging')
					;[
						...document.querySelectorAll(
							'.ct-panel-builder .ct-is-over'
						),
					].map((el) => el.classList.remove('ct-is-over'))
				},

				onMove: (event, originalEvent) => {
					let currentView = wp.customize.previewedDevice()

					if (currentView === 'tablet') {
						currentView = 'mobile'
					}

					if (event.from.closest('#ct-option-header-builder-items')) {
						Promise.resolve().then(() =>
							[
								...event.from.querySelectorAll(
									`[data-id="${event.dragged.dataset.id}"]`
								),
							].map((el) => {
								el.classList.remove('ct-builder-item')
								el.classList.add('ct-item-in-builder')
							})
						)
					}

					;[
						...document.querySelectorAll(
							'.ct-panel-builder .ct-is-over'
						),
					].map((el) => el.classList.remove('ct-is-over'))

					if (event.to) {
						event.to.classList.add('ct-is-over')
					}

					if (
						!isItemAllowedIn(
							event.dragged.dataset.id,
							event.to.dataset.id.split(':')[0],
							currentView
						)
					) {
						return false
					}
				},
				...options,
			}}
			onChange={(order, sortable, evt) => {
				// console.log('here is change', order, draggableId)

				if (draggableId === 'available-items') {
					return
				}

				onChange({
					id: draggableId,
					value: order.filter(
						(i) => i !== '__pointer__' && i !== '__filler__'
					),
				})
			}}
			tag={tagName}
			className={cls('ct-builder-items', className)}
			{...props}
			data-id={draggableId}>
			{['end', 'start-middle'].indexOf(draggableId.split(':')[1]) >
				-1 && <div data-id="__filler__" className="ct-filler"></div>}
			{hasPointers &&
				isDragging &&
				isItemAllowedIn(
					isDragging,
					draggableId.split(':')[0],
					currentView
				) && <div data-id="__pointer__" className="ct-pointer"></div>}
			{itemsOutput}
		</Sortable>
	)
}

export default DraggableItems
